import type { PropsWithChildren } from 'react'
import { buildImgSrc } from '@/helpers/image-url'
import { useIntl } from '@/intl'
import { useAnalytics } from '@/scopes/common/infrastructure/analytics/hooks'
import { createPDPClickEvent } from '@/scopes/products/infrastructure/analytics'
import { Button, Icon, Image, Modal, tk, Typography, zIndex } from '@sunwebgroup/ui-components'
import useEmblaCarousel from 'embla-carousel-react'
import { useCallback, useEffect, useState } from 'react'
import { styled } from 'styled-components'

const StyledModalHeader = styled(Modal.Header)`
  background: ${tk('color_semantic_bg_inverse_hover')};
  color: ${tk('color_semantic_text_on_color')};
  height: ${tk('spacing_4xl')};

  * {
    color: inherit;
  }
`

const StyledModal = styled(Modal.Content)`
  background: ${tk('color_semantic_bg_inverse_hover')};
  z-index: ${zIndex.modalOrMobilePopover + 50} !important;
  padding-bottom: ${tk('spacing_4xl')};

  ${Modal.Body} {
    @media ${tk('mq_tablet')} {
      padding-left: ${tk('spacing_xl')};
      padding-right: ${tk('spacing_xl')};
    }

    @media ${tk('mq_desktop_sm')} {
      padding-left: ${tk('spacing_5xl')};
      padding-right: ${tk('spacing_5xl')};
    }
  }
`

const StyledButton = styled(Button)`
  display: none;

  @media ${tk('mq_desktop_sm')} {
    display: inline-flex;
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
  }
`

const StyledLeftButton = styled(StyledButton)`
  left: 16px;

  // This is because the icon is causing wrong target for Google Analytics to be tracked
  ${Icon} {
    pointer-events: none;
  }
`

const StyledRightButton = styled(StyledButton)`
  right: 16px;

  // This is because the icon is causing wrong target for Google Analytics to be tracked
  ${Icon} {
    pointer-events: none;
  }
`

const Container = styled.section`
  position: relative;
  height: 100%;
`

const Slider = styled.div`
  overflow: hidden;
  height: 100%;
`

const SliderContainer = styled.div`
  display: flex;
  height: 100%;
`

const Slide = styled.div`
  flex: 0 0 100%;

  iframe {
    height: 100%;
    width: 100%;
  }

  img {
    object-fit: contain;
    height: 100%;
    background-color: ${tk('color_semantic_bg_inverse_hover')};
  }
`

const DetailImageWrapper = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  display: grid;
`

const DetailImage = styled(Image.Root)`
  height: 100%;
  object-fit: cover;
  width: 100%;

  img {
    filter: unset;
  }
`

export type DetailProps = PropsWithChildren<Omit<Modal.ModalProps, '$size' | 'title'>> & {
  startFromIndex?: number
  roomtypeLabel: string
  roomtypeImages: string[]
}

export function DetailLegacy({ startFromIndex = 0, roomtypeLabel, roomtypeImages, ...attrs }: DetailProps) {
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: false, startIndex: startFromIndex })
  const { sendEvent } = useAnalytics()
  const [slidesInView, setSlidesInView] = useState<number[]>([])
  const [currentSlideIndex, setCurrentSlideIndex] = useState(0)
  const intl = useIntl()
  const numberOfSlides = roomtypeImages.length

  function navigate(direction: 'previous' | 'next') {
    if (direction === 'previous') {
      emblaApi?.scrollPrev()
    }
    else {
      emblaApi?.scrollNext()
    }
  }

  const findSlidesInView = useCallback(() => {
    if (!emblaApi)
      return

    setSlidesInView((slidesInViewElements) => {
      if (slidesInViewElements.length === emblaApi.slideNodes().length) {
        emblaApi.off('slidesInView', findSlidesInView)
      }
      const inView = emblaApi.slidesInView().filter(index => !slidesInViewElements.includes(index))
      return slidesInViewElements.concat(inView)
    })
  }, [emblaApi, setSlidesInView])

  // Slider events
  const onSelect = useCallback(() => {
    if (emblaApi !== undefined) {
      const index = emblaApi.selectedScrollSnap()
      setCurrentSlideIndex(index)
    }
  }, [emblaApi])

  const onSettle = useCallback(() => {
    sendEvent(
      createPDPClickEvent({
        element: 'Slider',
        description: 'Roomtype image Gallery',
        result: 'Slide',
        type: roomtypeLabel,
      }),
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps -- We know that sendClickPdpEvent is not changing
  }, [])

  useEffect(() => {
    if (emblaApi === undefined)
      return

    setCurrentSlideIndex(emblaApi.selectedScrollSnap())
    emblaApi.on('select', onSelect)
    emblaApi.on('settle', onSettle)
    emblaApi.on('slidesInView', findSlidesInView)

    return () => {
      emblaApi.off('select', onSelect)
      emblaApi.off('settle', onSettle)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Only run once embla is mounted
  }, [emblaApi])

  const handleClose = () => {
    attrs.onClose?.()
  }

  return (
    <Modal.Root {...attrs} onClose={handleClose}>
      <StyledModal $size="fullscreen">
        <StyledModalHeader>
          <Modal.HeaderTitle text={`${currentSlideIndex + 1}  / ${numberOfSlides}`} as="span" />
          <Modal.HeaderCloseButton />
        </StyledModalHeader>
        <Modal.Body>
          <Container>
            <Slider ref={emblaRef}>
              <SliderContainer>
                {roomtypeImages.map((image, index) => (
                  <Slide key={index}>
                    <DetailImageWrapper>
                      <DetailImage
                        defaultSource={{ src: buildImgSrc(image, 480, 270), width: 480, height: 270 }}
                        loaderSource={{ src: buildImgSrc(image, 20) }}
                        sources={[
                          {
                            src: buildImgSrc(image, 1280, 960),
                            width: 1280,
                            height: 960,
                            breakpoint: 'breakpoint_tablet',
                          },
                        ]}
                        alternativeText={roomtypeLabel || ''}
                        startImmediateLoad={slidesInView.includes(index)}
                      >
                        <Image.Error>
                          <Icon glyph="IconNoImage" size="lg" />
                          <Typography $variant="body-lg-bold">
                            {intl.formatMessage({ id: 'roomtypes_image_error' })}
                          </Typography>
                        </Image.Error>
                      </DetailImage>
                    </DetailImageWrapper>
                  </Slide>
                ))}
              </SliderContainer>
            </Slider>
          </Container>
          {numberOfSlides > 0 && (
            <>
              <StyledLeftButton
                $hierarchy="secondary"
                $withoutColor
                onClick={() => navigate('previous')}
                disabled={currentSlideIndex === 0}
              >
                <Icon glyph="IconChevronLeft" />
              </StyledLeftButton>
              <StyledRightButton
                $hierarchy="secondary"
                $withoutColor
                onClick={() => navigate('next')}
                disabled={currentSlideIndex === numberOfSlides - 1}
              >
                <Icon glyph="IconChevronRight" />
              </StyledRightButton>
            </>
          )}
        </Modal.Body>
      </StyledModal>
    </Modal.Root>
  )
}
